import { Controller } from "@hotwired/stimulus"
import { formatNumber } from "../utils/number_format"

/**
 * Counter Controller
 *
 * Animates numbers from start to end value when scrolled into view.
 * Uses Intersection Observer to trigger animation on visibility.
 *
 * Usage:
 *   <div data-controller="counter"
 *        data-counter-start-value="0"
 *        data-counter-end-value="1000"
 *        data-counter-duration-value="2000">
 *     <span data-counter-target="value">0</span>
 *   </div>
 */
export default class extends Controller {
  static targets = ["value"]
  static values = {
    start: { type: Number, default: 0 },
    end: { type: Number, default: 100 },
    duration: { type: Number, default: 2000 },
    decimals: { type: Number, default: 0 },
    separator: { type: String, default: "," }
  }

  connect() {
    this.hasAnimated = false

    if ("IntersectionObserver" in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.hasAnimated) {
              this.animate()
              this.hasAnimated = true
            }
          })
        },
        { threshold: 0.1 }
      )

      this.observer.observe(this.element)
    } else {
      // Fallback for browsers without IntersectionObserver
      this.animate()
    }
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  animate() {
    const start = this.startValue
    const end = this.endValue
    const duration = this.durationValue
    const decimals = this.decimalsValue
    const startTime = performance.now()

    const updateCounter = (currentTime) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)

      // Easing function (ease-out cubic)
      const easeOut = 1 - Math.pow(1 - progress, 3)

      const currentValue = start + (end - start) * easeOut
      this.updateDisplay(currentValue, decimals)

      if (progress < 1) {
        requestAnimationFrame(updateCounter)
      } else {
        this.updateDisplay(end, decimals)
      }
    }

    requestAnimationFrame(updateCounter)
  }

  updateDisplay(value, decimals) {
    if (!this.hasValueTarget) return

    this.valueTarget.textContent = formatNumber(value, {
      decimals,
      separator: this.separatorValue
    })
  }
}
